export const TRANSFORMER_33KV_ITEM_NAME = 'Transformer:100KVA 33/0.4KV,Copper';
export const TRANSFORMER_11KV_ITEM_NAME = 'Transformer:200KVA 33/0.4KV,Copper';

/** Canonical label for earth wire lines (OHGW suffix and spacing variants merge here). */
export const CABLE_7WIRE_EARTH_WIRE_ITEM_NAME = '7wire x 2.0mm Earth wire';

/** PVC / SiC / G·Y spelling variants → one label for PO quantity totals. */
export const CABLE_35MM2_SC_CU_ITEM_NAME = 'PVC Insulated 35 mm2 S/C Cu Cable G/Y';

/** @deprecated Use CABLE_35MM2_SC_CU_ITEM_NAME */
export const CABLE_35MM2_SC_CU_GY_ITEM_NAME = CABLE_35MM2_SC_CU_ITEM_NAME;

/** ACSR Dog / mm² variants → one label for PO quantity totals. */
export const CABLE_100SQMM_ACSR_ITEM_NAME = '100sqmm ACSR';

/** 50 mm² × 4-core ABC variants → one label for PO quantity totals. */
export const CABLE_4X50SQMM_ABC_ITEM_NAME = '4x50sq.mm ABC Cable';

/** 50 mm² × 2-core ABC variants (e.g. 2x50sq.mm ABCCable → spaced label). */
export const CABLE_2X50SQMM_ABC_ITEM_NAME = '2x50sq.mm ABC Cable';

const normalizeSpacing = (value) =>
  String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const isFullTransformerSpec = (normalized) =>
  normalized.includes('kva') || normalized.includes('/0.4');

/** True for generic short labels like "Transformer 33kV" — not full KVA specs already on the PO. */
export function isTransformer33kVItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName);
  if (!normalized.includes('transformer') || !normalized.includes('33')) return false;
  if (isFullTransformerSpec(normalized)) return false;
  return true;
}

/** True for generic short labels like "Transformer 11kV" — not full KVA specs already on the PO. */
export function isTransformer11kVItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName);
  if (!normalized.includes('transformer') || !normalized.includes('11')) return false;
  if (isFullTransformerSpec(normalized)) return false;
  return true;
}

/** 7wire × 2.0mm earth wire / OHGW — one name so PO quantities sum together. */
export function is7WireEarthWireItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName);
  if (!normalized.includes('earth wire')) return false;
  if (!normalized.includes('2.0') && !normalized.includes('2.0mm')) return false;
  return normalized.includes('7wire') || normalized.includes('7 wire');
}

/** 35 mm² single-core copper (e.g. PVCInsulated … G/Y or without G/Y). */
export function is35mm2ScCuCableItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName).replace(/-/g, ' ');
  if (!normalized.includes('35') || !normalized.includes('mm2')) return false;
  if (!normalized.includes('cu') || !normalized.includes('cable')) return false;
  const hasCore =
    normalized.includes('sic') ||
    normalized.includes('s/c') ||
    normalized.includes('s / c');
  if (!hasCore && !normalized.includes('pvcinsulated')) return false;
  return (
    normalized.includes('insulated') ||
    normalized.includes('pvcinsulated') ||
    normalized.includes('insulated 35 mm2 s/c cu cable')
  );
}

/** @deprecated Use is35mm2ScCuCableItemName */
export const is35mm2ScCuCableGyItemName = is35mm2ScCuCableItemName;

/** 100 mm² / 100sqmm ACSR (e.g. 100mm² ACSR "Dog"). */
export function is100sqmmAcsrItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName)
    .replace(/[""]/g, '')
    .replace(/²/g, '2');
  if (!normalized.includes('acsr')) return false;
  if (!normalized.includes('100')) return false;
  return (
    normalized.includes('100mm2') ||
    normalized.includes('100 mm2') ||
    normalized.includes('100sqmm') ||
    normalized.includes('100 sqmm') ||
    normalized.includes('100 sq mm') ||
    normalized.includes('dog')
  );
}

/** 50 mm² × 2-core ABC (e.g. 2x50sq.mm ABCCable). */
export function is2x50sqmmAbcCableItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName).replace(/²/g, '2');
  if (!normalized.includes('abc')) return false;
  if (!normalized.includes('50')) return false;
  if (
    normalized.includes('4core') ||
    normalized.includes('4 core') ||
    normalized.includes('4x50') ||
    normalized.includes('4 x 50')
  ) {
    return false;
  }
  const hasTwoCore =
    normalized.includes('2core') ||
    normalized.includes('2 core') ||
    normalized.includes('2x50') ||
    normalized.includes('2 x 50');
  if (!hasTwoCore) return false;
  return (
    normalized.includes('50mm2') ||
    normalized.includes('50 mm2') ||
    normalized.includes('50sq') ||
    normalized.includes('abccable') ||
    normalized.includes('abc cable') ||
    normalized.includes('2x50sq.mm')
  );
}

/** 50 mm² × 4-core ABC (e.g. 50mm² x 4core ABC). */
export function is4x50sqmmAbcCableItemName(itemName = '') {
  const normalized = normalizeSpacing(itemName).replace(/²/g, '2');
  if (!normalized.includes('abc')) return false;
  if (!normalized.includes('50')) return false;
  const hasFourCore =
    normalized.includes('4core') ||
    normalized.includes('4 core') ||
    normalized.includes('4x50') ||
    normalized.includes('4 x 50');
  if (!hasFourCore) return false;
  return (
    normalized.includes('50mm2') ||
    normalized.includes('50 mm2') ||
    normalized.includes('50sq') ||
    normalized.includes('abc cable') ||
    normalized.includes('4x50sq.mm abc cable')
  );
}

/**
 * Correct known PO item labels (e.g. "Transformer 33kV" → full spec name).
 * Also applies Lot 8 Morogoro cable naming fixes.
 */
export function correctPoItemName(lot, itemName) {
  if (!itemName) return itemName;

  const normalized = normalizeSpacing(itemName);
  if (is7WireEarthWireItemName(itemName)) {
    return CABLE_7WIRE_EARTH_WIRE_ITEM_NAME;
  }
  if (is35mm2ScCuCableItemName(itemName)) {
    return CABLE_35MM2_SC_CU_ITEM_NAME;
  }
  if (is100sqmmAcsrItemName(itemName)) {
    return CABLE_100SQMM_ACSR_ITEM_NAME;
  }
  if (is4x50sqmmAbcCableItemName(itemName)) {
    return CABLE_4X50SQMM_ABC_ITEM_NAME;
  }
  if (is2x50sqmmAbcCableItemName(itemName)) {
    return CABLE_2X50SQMM_ABC_ITEM_NAME;
  }
  if (isTransformer33kVItemName(itemName)) {
    return TRANSFORMER_33KV_ITEM_NAME;
  }
  if (isTransformer11kVItemName(itemName)) {
    return TRANSFORMER_11KV_ITEM_NAME;
  }

  const isMorogoro = String(lot || '').toLowerCase().includes('lot 8');
  if (!isMorogoro) return itemName;
  if (normalized.includes('2.5mm') && normalized.includes('2core') && normalized.includes('flexible cable')) {
    return '2.5mm² x 2core Flexible Cable';
  }
  if (normalized.includes('2.5mm') && normalized.includes('2 core') && normalized.includes('flexible cable')) {
    return '2.5mm² x 2core Flexible Cable';
  }
  if (normalized.includes('2.5mm') && normalized.includes('3 core') && normalized.includes('flex cable')) {
    return '2.5mm² x 2core Flexible Cable';
  }
  return itemName;
}

/** Normalize every name field on a PO line item object. */
export function correctPoLineItem(lot, item = {}) {
  if (!item || typeof item !== 'object') return item;

  const nameFields = ['itemName', 'name', 'item', 'description'];
  let changed = false;
  const next = { ...item };

  nameFields.forEach((field) => {
    if (!next[field]) return;
    const corrected = correctPoItemName(lot, next[field]);
    if (corrected !== next[field]) {
      next[field] = corrected;
      changed = true;
    }
  });

  if (!changed) return item;

  if (!next.itemName) {
    const primary = nameFields.map((field) => next[field]).find(Boolean);
    if (primary) next.itemName = primary;
  }

  return next;
}

export function correctPoItems(lot, items = []) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => correctPoLineItem(lot, item));
}
